import * as _ from 'lodash';
import { ConfigConstants } from '../../config/config-constants';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { MultilineTextModelOptions } from '../options/multiline-text-model-options';
import { PrimitiveModel } from './primitive.model';
import REGEX = ConfigConstants.internalInfoTextLineRegex;

export class InternalInfoMultilineTextModel extends PrimitiveModel {
    declare public value: string;
    public constructor(
        name: string,
        value = '',
        public functionalLoc: string,
        public textId: string,
        public options = new MultilineTextModelOptions(),
    ) {
        super(name, value, options);
        // if format is required then text will be formatted when model loads
        // use case: trouble text in AA data which read from NDP and should be formatted
        if (this.options.formatMultilineTextPattern) {
            this.value = this.getFormattedText();
        }
    }

    /**
     * The lines has to be auto formatted using the modelOptions.lineLength value
     * This returns the string result of formatted lines
     */
    public getFormattedText(): string {
        const lines = this.getLines();
        // return the line array as string joined with new line
        return lines.join('\n');
    }

    /**
     * get the lines array created using modelOptions.lineLength value
     */
    public getLines(): string[] {
        let lines: string[] = [];
        let regexLines: Array<RegExpMatchArray | string[]> = [];
        let regex = new RegExp(`.{1,${this.options.lineLength}}`, 'g');

        if (this.options.formatMultilineTextPattern) {
            // https://www.regextester.com/?fam=111048
            // regular expression allow the string to break without breaking the word if whitespace exists
            // if there is no whitespace found the word will break
            // pattern is controlled from GW to allow different pattern based on countries
            regex = new RegExp(this.options.formatMultilineTextPattern, 'g');
        }

        // split the text value by line breaks
        if (this.options.splitLines) {
            lines = this.value.split('\n');

            // Split lines that are longer than the given limit. The check for 'matched' is necessary to emit an empty string
            // when the input line only contains a line break:
            //
            // > foo = "This is a longtext\n\n which is split across multiple lines\n"
            // > foo.split('\n').map(l => l.match(new RegExp('.{1,120}', 'g')))
            //
            // Results in: [["This is a longtext"], null, ["which is split across multiple lines"], null]
            //                                      ^^^^                                            ^^^^
            // The null values are a problem because they end up in the data for SAP which expects only strings
            //
            // Returning and empty string in an array gives us the correct result:
            //
            // > foo.split('\n').map(l => l.match(regex) || [''])
            //
            // Results in: [["This is a longtext"], [""], ["which is split across multiple lines"], [""]]
            regexLines = lines.map((line) => line.match(regex) || ['']);

            // flatten the array returned by the previous steps
            lines = _.flatten(regexLines);
            lines = lines.map((line) => line.trim());
        } else {
            lines = [this.value];
        }
        return lines;
    }

    public trimLines(): void {
        if (this.value && this.value.length > 0) {
            const lines = this.value.split('\n');
            this.value = lines.map((line) => line.trim()).join('\n');
        }
    }

    /**
     * Scenario
     * User deletes all the values/Text in any category example: Parking category and submits data,
     * then before submitting data to backend, the Header-Info "05.20.2022 by Z0042DNE" is concatenated and sent to backend
     * Now, from backend we get only "05.20.2022 by Z0042DNE" as user deleted everything
     * This Header info is already displayed in the bottom of the screen and is not necessary to display in the Parking text area.
     * Ticket No: SHCNGMP58-2534
     */
    public getTextToBeDisplayedInTextArea(): string {
        const text = this.getLines();
        if (text.length === 1 && text[0].match(REGEX.headerInfo)) {
            return '';
        }
        return this.value;
    }

    /**
     * Convert the internal data into an object suitable for OData exchange with SAP
     *
     * @returns {object} the internal value in a format suitable for SAP
     */
    public getOutModel(): ODataObject {
        const outModel = super.getOutModel();

        outModel[this.name] = this.getLines().map((line) => {
            return {
                TextLine: line,
                TextID: this.textId,
                TextName: this.functionalLoc,
                TextObject: 'IFLOT',
                Language: 'EN',
            };
        });
        return outModel;
    }
}

export interface ODataInfoTextLineOutModel extends ODataObject {
    TextLine: string;
    TextID: string;
    TextName: string;
    TextObject: string;
    Language: string;
}
